import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../api/client';
import { getVisitorId, trackEvent } from '../lib/track';
import { isPayFirstVisitor } from '../lib/experiment';

// Pay-first A/B experiment. Fetches the display-safe experiment config from
// /api/site-config once, buckets THIS visitor deterministically from their
// stable visitorId (so the variant never flips between visits), and records the
// assignment once for the funnel. `ready` gates any UI that must not flash the
// wrong variant — until the config resolves we render as the control (normal
// free-preview) flow, which is the safe default if the config is unreachable.
const ExperimentContext = createContext({ ready: false, payFirst: false });

// Ad-driven override: landing on ANY page with ?preview=0 (the AddToCart-
// optimized campaign appends it to its link URLs) forces this browser into the
// pay-first flow — no free preview — regardless of the experiment flag, and
// sticks for later visits via localStorage so the visitor doesn't regain the
// preview by navigating away from the landing URL. ?preview=1 clears the
// override (escape hatch for testing). Visitors who never hit such a URL are
// completely unaffected.
const FORCE_KEY = 'iamhero_force_payfirst';
function resolveForcedPayFirst() {
  let param = null;
  try {
    param = new URLSearchParams(window.location.search).get('preview');
  } catch { /* no window/URL — treat as no param */ }
  try {
    if (param === '0') localStorage.setItem(FORCE_KEY, '1');
    else if (param === '1') localStorage.removeItem(FORCE_KEY);
    return localStorage.getItem(FORCE_KEY) === '1';
  } catch {
    // localStorage blocked (private mode) — honor the URL for this page load.
    return param === '0';
  }
}

export function ExperimentProvider({ children }) {
  const [state, setState] = useState({ ready: false, payFirst: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const forced = resolveForcedPayFirst();
      let payFirst = forced;
      if (!forced) {
        try {
          const cfg = await api.get('/api/site-config');
          const pf = cfg?.payFirst || {};
          payFirst = !!pf.enabled && isPayFirstVisitor(getVisitorId(), pf.percent);
        } catch { /* config unreachable → control (safe default) */ }
      }
      if (cancelled) return;
      setState({ ready: true, payFirst });
      // Record the assignment once per (visitor, variant) so the funnel can be
      // measured. Re-logs only if the variant actually changes (e.g. admin edits
      // the percent), never on every page load. URL-forced visitors get their
      // own variant name so the ad cohort is separable from the A/B experiment.
      try {
        const variant = forced ? 'pay_first_url' : (payFirst ? 'pay_first' : 'control');
        if (localStorage.getItem('iamhero_experiment_bucket') !== variant) {
          localStorage.setItem('iamhero_experiment_bucket', variant);
          trackEvent('experiment_bucket', { name: variant });
        }
      } catch { /* localStorage blocked — skip the one-time log */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return <ExperimentContext.Provider value={state}>{children}</ExperimentContext.Provider>;
}

export function useExperiment() {
  return useContext(ExperimentContext);
}
